'use client';

import { useState } from 'react';
import { BonusShopProductType } from '@/api/cms/bonusShop';
import { useNavigation } from '@/hooks/navigation';
import {
  sendBonusShopExchangeFailureEvent,
  sendBonusShopExchangeSuccessEvent,
  sendBonusShopProductDetailsEvent,
} from '@/modules/googleAnalytics';
import { PromotionPageTabs } from '@/modules/promotions/components/PromotionsPage';
import { useGetCommonStore } from '@/store';
import { getExchangeEventData } from '../helpers';
import type { BonusShopProduct, BonusShopProductVersion } from '../types';
import { ExchangeStatus, useBonusShopExchange } from './useBonusShopExchange';

const BONUS_CABINET_PATH = `/promotions?tab=${PromotionPageTabs.ACTIVE_BONUSES}`;
const ACTIVATABLE_TYPES = [
  BonusShopProductType.FREE_SPINS,
  BonusShopProductType.CASH_BONUS,
];

export const useBonusShopProductExchange = (
  product: BonusShopProduct,
  version?: BonusShopProductVersion
) => {
  const [isExchangeOpen, setIsExchangeOpen] = useState(false);
  const [exchanged, setExchanged] = useState<{
    product: BonusShopProduct;
    version?: BonusShopProductVersion;
  }>();

  const useCommonStore = useGetCommonStore();
  const compPoints = useCommonStore.use.compPoints();
  const { navigate } = useNavigation();
  const { currency, exchange, isPending, status, resetStatus, finishExchange } =
    useBonusShopExchange();

  const balance = compPoints?.chargeable?.points || 0;
  const isError = status === ExchangeStatus.ERROR;
  const eventData = getExchangeEventData(product, currency, version);

  const isResultShown =
    isError ||
    (status === ExchangeStatus.SUCCESS &&
      product.type !== BonusShopProductType.LOOTBOX);

  const openExchange = () => {
    setIsExchangeOpen(true);
    sendBonusShopProductDetailsEvent({ ...eventData, cp_balance: balance });
  };

  const closeExchange = () => setIsExchangeOpen(false);

  const closeResult = () => {
    setIsExchangeOpen(false);
    setExchanged(undefined);
    finishExchange();
  };

  const confirmExchange = async () => {
    setExchanged({ product, version });

    const { error, isSuccess } = await exchange(product, version);

    if (isSuccess) sendBonusShopExchangeSuccessEvent(eventData);
    else sendBonusShopExchangeFailureEvent({ ...eventData, error });

    if (isSuccess && product.type === BonusShopProductType.LOOTBOX)
      closeResult();
  };

  const handleResultAction = () => {
    if (isError) {
      resetStatus();
      return;
    }

    closeResult();

    if (ACTIVATABLE_TYPES.includes(product.type))
      navigate(BONUS_CABINET_PATH, true);
  };

  return {
    balance,
    exchanged,
    isError,
    isExchangeOpen: isExchangeOpen && status === ExchangeStatus.IDLE,
    isPending,
    isResultShown,
    openExchange,
    closeExchange,
    confirmExchange,
    closeResult,
    handleResultAction,
  };
};
